// FinalOffer.tsx
import React, { useEffect, useState } from 'react';
import axios from "../lib/axios";
import { useNavigate } from 'react-router-dom';
import { Button } from "@/components/ui/button";
import CallbackHelp from "@/components/CallbackHelp";
import { API_BASE_URL } from '../config';
import { useLoader } from "../contexts/LoaderContext";
import JourneyHeader from "@/components/JourneyHeader";

interface LoanDetails {
	loanAmount: number,
	procFeePerc: number,
	procFeeAmt: number,
	gstOnProcFee: number,
	deduction: number,
	deductionText: number,
	amtYouGet: number,
	interestRateMonthlyShow: number,
	amtYouPay: number,
	totalInterestPayable: number,
	daily_installment: number,
	tenure: number,
	repay_type: string,
	bankAccount: string,
	isBankAccountPrepopulated: boolean,
}

const FinalOffer = () => {
	const navigate = useNavigate();
	const [isCallbackOpen, setIsCallbackOpen] = useState(false);
	const { setLoading } = useLoader();
	const [isBankDataLoaded, setIsBankDataLoaded] = useState(false);
	const [isLoanOfferDataLoaded, setIsLoanOfferDataLoaded] = useState(false);
	const [loanAmount, setLoanAmount] = useState<number>(0);
	const [selectedTenure, setSelectedTenure] = useState<number>(0);
	const [repaymentMethod, setRepaymentMethod] = useState<'fixed' | 'percentage'>('fixed');
	const [nextPageUrl, setNextPageUrl] = useState<string>('');

	const formatCurrency = (amount: number) => {
		return new Intl.NumberFormat('en-IN', {
			style: 'currency',
			currency: 'INR',
			maximumFractionDigits: 0
		}).format(amount);
	};

	const [loanDetails, setLoanDetails] = useState<LoanDetails>({
		loanAmount: 0,
		procFeePerc: 0,
		procFeeAmt: 0,
		gstOnProcFee: 0,
		deduction: 0,
		deductionText: 0,
		amtYouGet: 0,
		interestRateMonthlyShow: 0,
		amtYouPay: 0,
		totalInterestPayable: 0,
		daily_installment: 0,
		tenure: 0,
		repay_type: "EMI",
		bankAccount: "XXXX XXXX XXXX",
		isBankAccountPrepopulated: false,
	});

	const getLoanSummaryAndSchedule = async (values: {
		loanAmount: number;
		selectedTenure: number;
		repaymentMethod: 'fixed' | 'percentage';
	}) => {
		try {
			const res = await axios.post(`${API_BASE_URL}/api/repayment_schedule/`, values);
			const { summary } = res.data;

			setLoanDetails({
				loanAmount: summary.offer_amount,
				procFeePerc: summary.proc_fee_pct,
				procFeeAmt: summary.proc_fee_amt,
				gstOnProcFee: summary.gst_on_proc_amt,
				deduction: summary.deduction,
				deductionText: summary.deduction_text,
				amtYouGet: summary.net_disbursal,
				interestRateMonthlyShow: summary.interest_rate_monthly_to_show,
				amtYouPay: summary.offer_amount_winterest,
				totalInterestPayable: summary.offer_amount_winterest - summary.offer_amount,
				daily_installment: summary.epi,
				tenure: summary.loan_tenure,
				repay_type: summary.repay_type,
				bankAccount: "XXXX XXXX XXXX",
				isBankAccountPrepopulated: false,
			});
			setTimeout(() => {
				setIsLoanOfferDataLoaded(true);
			}, 100);
		} catch (err) {
			console.error("Error syncing with backend:", err);
		}
	};

	useEffect(() => {
		if (isBankDataLoaded && isLoanOfferDataLoaded) {
			setLoading(false);
		}
		else {
			setLoading(true);
		}
	}, [isBankDataLoaded, isLoanOfferDataLoaded])

	useEffect(() => {
		axios
			.post(`${API_BASE_URL}/api/get_final_offer_details/`, {}, { maxRedirects: 0 })
			.then(async (res) => {
				if (res.request.responseURL.includes("https://apply.intellendtech.com/accounts/login/")) {
					navigate("/session-timeout");
				}
				if (res.status === 200) {
					setLoanAmount(parseInt(res.data['loan_amount']));
					setSelectedTenure(res.data['loan_tenure']);
					setRepaymentMethod(res.data['repayment_method']);
					setNextPageUrl(res.data['next_page_url']);
				}
			})
			.catch((err) => console.error("Fetch error:", err))
			.finally(() => {
				setTimeout(() => { setIsBankDataLoaded(true) }, 100);
			});
	}, []);

	useEffect(() => {
		let values = { loanAmount, selectedTenure, repaymentMethod }
		getLoanSummaryAndSchedule(values);
	}, [isBankDataLoaded])

	const handleProceed = async () => {
		setLoading(true);
		try {
			// Advance journey from FINAL_OFFER → next stage in journey template
			const res = await axios.post(`${API_BASE_URL}/api/accept_offer_by_lender/`);
			const next = res.data?.next_page_url || nextPageUrl || '/selfie-photo-loc-verification';
			navigate(next);
		} catch (err) {
			console.error("Failed to advance journey from final offer:", err);
			// Fallback: navigate anyway so user isn't stuck
			navigate(nextPageUrl || '/selfie-photo-loc-verification');
		} finally {
			setLoading(false);
		}
	};

	return (
		<div className="min-h-screen bg-intellend-white">
			<JourneyHeader />

			<main className="container mx-auto px-4 py-8 max-w-3xl">
				<div className="bg-white shadow-md rounded-lg p-6 mb-8">
					<h2 className="text-2xl font-bold text-intellend-green mb-6 text-center">Your Final Loan Offer</h2>

					<div className="space-y-4">
						<div className="mb-6 p-4 bg-gray-50 rounded-lg">
							<div className="flex justify-between items-center">
								<h3 className="font-semibold text-lg">Loan Amount</h3>
								<div className="text-xl font-bold">{formatCurrency(loanDetails.loanAmount)}</div>
							</div>
						</div>
						<div className="mb-6 p-4 bg-gray-50 rounded-lg">
							<div className="flex justify-between items-center">
								<div>
									<h3 className="font-semibold text-lg">Deduction</h3>
									<div className='text-xs text-gray-500'>{loanDetails.deductionText}</div>
								</div>
								<div className="text-xl font-bold whitespace-nowrap">
									-&nbsp;{formatCurrency(loanDetails.deduction)}
								</div>
							</div>
						</div>
						<div className="mb-6 p-4 bg-gray-50 rounded-lg">
							<div className="flex justify-between items-center">
								<div>
									<h3 className="font-semibold text-lg">Amount You Get</h3>
									<div className='text-xs text-gray-500'>Loan Amount - Deduction</div>
								</div>
								<div className="text-xl font-bold">
									{formatCurrency(loanDetails.amtYouGet)}
								</div>
							</div>
						</div>
						<div className="mb-6 p-4 bg-gray-50 rounded-lg">
							<div className="flex justify-between items-center">
								<div>
									<h3 className="font-semibold text-lg">Amount You Pay</h3>
									<div className='text-xs text-gray-500'>@{loanDetails.interestRateMonthlyShow}% per month</div>
								</div>
								<div className="text-xl font-bold">
									{formatCurrency(loanDetails.amtYouPay)}
								</div>
							</div>
						</div>
						<div className="mb-6 p-4 bg-gray-50 rounded-lg">
							<div className="flex justify-between items-center">
								<h3 className="font-semibold text-lg">Total Interest Payable</h3>
								<div className="text-xl font-bold">
									{formatCurrency(loanDetails.amtYouPay - loanDetails.loanAmount)}
								</div>
							</div>
						</div>
						<div className="mb-6 p-4 bg-gray-50 rounded-lg">
							<div className="flex justify-between items-center">
								<h3 className="font-semibold text-lg">{loanDetails.repay_type === 'EDI' ? 'Daily' : 'Monthly'} Installment</h3>
								<div className="text-xl font-bold">
									{formatCurrency(loanDetails.daily_installment)}
								</div>
							</div>
						</div>
						<div className="mb-6 p-4 bg-gray-50 rounded-lg">
							<div className="flex justify-between items-center">
								<h3 className="font-semibold text-lg">Loan Tenure</h3>
								<div className="text-xl font-bold">
									{Number(loanDetails.tenure) || 0}{" "}
									{loanDetails.repay_type === 'EDI'
										? (Number(loanDetails.tenure) === 1 ? "Day" : "Days")
										: (Number(loanDetails.tenure) === 1 ? "Month" : "Months")}
								</div>
							</div>
						</div>
						<p className="text-xs text-gray-500 italic">
							* All values, figures, and illustrations provided in this application are tentative and subject to verification, approval, and change without prior notice. Final loan terms, interest rates, fees, and repayment schedules will be confirmed only after completion of the evaluation and sanction process by the lender. Final (net) amount disbursed to your account may be different due to deduction of broken period interest, based on the exact disbursement date of your loan.
						</p>

						<div className="flex flex-col md:flex-row gap-4 pt-4">
							<Button
								type="button"
								variant="outline"
								onClick={() => setIsCallbackOpen(true)}
								className="w-full md:w-auto"
							>
								Request Callback
							</Button>
							<Button
								className="w-full md:w-auto md:ml-auto bg-intellend-green hover:bg-intellend-green/90"
								onClick={handleProceed}
							>
								Confirm and Proceed to Verifications
							</Button>
						</div>
					</div>
				</div>
			</main>

			<CallbackHelp
				isOpen={isCallbackOpen}
				setIsOpen={setIsCallbackOpen}
				showOk={false}
				showCallUs={true}
				showWhatsappUs={true}
				silentlyTrigger={true}
				title="Intellend Support"
			/>
		</div>
	);
};

export default FinalOffer;
